import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { Download, Printer } from 'lucide-react';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';

const BadgePreviewModal = ({ 
  eventId, 
  registrationId, 
  isOpen, 
  onClose, 
  registrationData,
  templateId: initialTemplateId 
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplateId || 'minimal');
  const [templates, setTemplates] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useToast();

  // Helper function to get form field value with fallback variations
  const getFormFieldValue = (formData, fieldName) => {
    if (!formData) return 'N/A';
    
    // Try different variations of the field name
    const variations = [
      fieldName, // Original field name
      fieldName.toLowerCase(), // Lowercase version
      fieldName.replace(/\s+/g, '').toLowerCase(), // No spaces, lowercase
      fieldName.replace(/\s+/g, '_').toLowerCase(), // Underscore, lowercase
    ];
    
    for (const variation of variations) {
      if (formData[variation] !== undefined) {
        return formData[variation];
      }
    }
    
    return 'N/A';
  };

  // Fetch badge templates
  useEffect(() => {
    if (isOpen && eventId) {
      fetchTemplates();
    }
  }, [isOpen, eventId]);

  // Set initial template when templates are loaded
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplate) {
      setSelectedTemplate(templates[0].templateId);
    }
  }, [templates, selectedTemplate]);

  // Generate preview when template changes
  useEffect(() => {
    if (selectedTemplate && registrationId && eventId) {
      generatePreview();
    }
  }, [selectedTemplate, registrationId, eventId]);

  const fetchTemplates = async () => {
    try {
      const templatesData = await ApiService.getBadgeTemplates(eventId);
      setTemplates(templatesData || []);
      if (templatesData && templatesData.length > 0) {
        setSelectedTemplate(templatesData[0].templateId);
      } else {
        // Fallback to default template if no templates are available
        setSelectedTemplate('minimal');
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      showError('Failed to load badge templates');
      // Fallback to default template
      setSelectedTemplate('minimal');
    }
  };

  const generatePreview = async () => {
    if (!eventId) return;
    
    setLoading(true);
    try {
      let previewBlob;
      
      if (registrationId && registrationId !== 'preview') {
        // For real users, use the user-specific badge print API
        previewBlob = await ApiService.printUserBadge(eventId, registrationId);
      } else {
        // For preview mode, use the configuration preview API
        const badgeConfig = await ApiService.getBadgeConfig(eventId);
        if (!badgeConfig) {
          throw new Error('No badge configuration found');
        }
        previewBlob = await ApiService.previewBadgeConfig(eventId, badgeConfig);
      }
      
      // Convert blob to URL for display
      const previewUrl = URL.createObjectURL(previewBlob);
      setPreviewData({ previewUrl, blob: previewBlob });
    } catch (error) {
      console.error('Failed to generate preview:', error);
      showError('Failed to generate badge preview');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      if (previewData?.blob) {
        const url = window.URL.createObjectURL(previewData.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `badge_${getFormFieldValue(registrationData, 'Name') || 'badge'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        showSuccess('Badge downloaded successfully');
      } else {
        throw new Error('No preview data available');
      }
    } catch (error) {
      console.error('Failed to download badge:', error);
      showError('Failed to download badge');
    }
  };

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Badge Print</title>
          <style>
            body { margin: 0; padding: 20px; }
            .badge-container { display: flex; gap: 20px; }
            .badge { width: 300px; height: 200px; border: 1px solid #ccc; }
            .badge img { width: 100%; height: 100%; object-fit: contain; }
            @media print {
              body { margin: 0; }
              .badge-container { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="badge-container">
            ${previewData?.frontPreviewBase64 ? 
              `<div class="badge"><img src="${previewData.frontPreviewBase64}" alt="Front Badge" /></div>` : ''}
            ${previewData?.backPreviewBase64 ? 
              `<div class="badge"><img src="${previewData.backPreviewBase64}" alt="Back Badge" /></div>` : ''}
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader showClose={false}>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Badge Preview</DialogTitle>
              <DialogDescription>
                Preview and download badges for {getFormFieldValue(registrationData, 'Name') || 'this registration'}
              </DialogDescription>
            </div>
            <DialogClose onClick={onClose} />
          </div>
        </DialogHeader>

        <div className="p-6 space-y-4">
          {/* Template Selection */}
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium">Template:</label>
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.templateId} value={template.templateId}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>


          {/* Preview Display */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : previewData?.previewUrl ? (
            <div className="space-y-4">
              {/* PDF Badge Preview */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex justify-center">
                    <iframe
                      src={previewData.previewUrl}
                      title="Badge Preview"
                      className="w-full h-96 border rounded-lg"
                      style={{ minHeight: '400px' }}
                    />
                  </div>
                </CardContent>
              </Card>

            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <div className="mb-4">
                <p>Failed to generate badge preview</p>
                <p className="text-sm mt-2">Please try selecting a different template or check your connection.</p>
              </div>
              <Button 
                variant="outline" 
                onClick={generatePreview}
                disabled={loading}
              >
                Retry Preview
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button 
            variant="outline" 
            onClick={handlePrint}
            disabled={!previewData}
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button 
            onClick={handleDownload}
            disabled={!previewData}
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BadgePreviewModal;
